// Static marketplace data for the redesigned landing page.
// Realistic placeholder content — mirrors the data used elsewhere on the site
// (see src/pages/Courses.jsx) so course cards stay consistent across the app.

import Accounting from "../../assets/accounting.jpeg";

// ── Helpers ────────────────────────────────────────────────────────────────

export const formatNaira = (value) =>
  `₦${Number(value || 0).toLocaleString("en-NG")}`;

export const formatStudents = (value) => {
  const n = Number(value || 0);
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return String(n);
};

export const discountPercent = (price, oldPrice) => {
  if (!oldPrice || oldPrice <= price) return 0;
  return Math.round(((oldPrice - price) / oldPrice) * 100);
};

// ── Categories / levels / price bands / sort options ───────────────────────

export const COURSE_CATEGORIES = [
  "Artificial Intelligence (AI)",
  "Python",
  "Microsoft Excel",
  "AI Agents & Agentic AI",
  "Digital Marketing",
  "Amazon AWS",
  "Cyber Security",
  "Web Development",
  "Data Science",
  "Machine Learning",
  "Blockchain",
  "Graphic Design",
  "Cloud Computing",
  "Project Management",
  "Entrepreneurship",
];

export const COURSE_LEVELS = ["Beginner", "Intermediate", "Advanced", "All Levels"];

export const PRICE_BANDS = [
  { id: "all", label: "Any price", test: () => true },
  { id: "under10", label: "Under ₦10,000", test: (p) => p < 10000 },
  { id: "10to13", label: "₦10,000 – ₦13,000", test: (p) => p >= 10000 && p <= 13000 },
  { id: "over13", label: "Above ₦13,000", test: (p) => p > 13000 },
];

export const RATING_BANDS = [
  { id: "all", label: "Any rating", test: () => true },
  { id: "4plus", label: "4.5 & up", test: (r) => r >= 4.5 },
  { id: "4", label: "4.0 & up", test: (r) => r >= 4.0 },
  { id: "4.8", label: "4.8 & up", test: (r) => r >= 4.8 },
];

export const SORT_OPTIONS = [
  { id: "popular", label: "Most popular" },
  { id: "newest", label: "Newest" },
  { id: "rating", label: "Highest rated" },
  { id: "price-asc", label: "Price: low to high" },
  { id: "price-desc", label: "Price: high to low" },
];

// ── Courses ────────────────────────────────────────────────────────────────
// `monthsNew` is a synthetic recency rank (smaller = newer) used for the
// "Newest" sort and the "Newly Added" rail.

export const LANDING_COURSES = [
  {
    id: "ai-automation-agentic-ai-bootcamp",
    title: "Complete AI Automation And Agentic AI Bootcamp With n8n",
    image: Accounting,
    instructor: "Akan Nduonofit",
    category: "Artificial Intelligence (AI)",
    level: "All Levels",
    rating: 4.5,
    reviews: 387,
    students: 12400,
    hours: 25.5,
    lessons: 96,
    price: 10900,
    oldPrice: 21900,
    bestseller: true,
    updated: "January 2026",
    monthsNew: 1,
    description:
      "Build AI-driven automation and Agentic AI workflows using n8n with real-world projects.",
    features: [
      "Build AI automation workflows using n8n and integrate LLMs for intelligent actions.",
      "Connect n8n with APIs, databases, and apps to build end-to-end AI pipelines.",
    ],
  },
  {
    id: "python-programming-masterclass",
    title: "Python Programming Masterclass",
    image: "https://images.unsplash.com/photo-1526379095098-d400fd0bf935?auto=format&fit=crop&w=600&q=80",
    instructor: "Python Academy",
    category: "Python",
    level: "All Levels",
    rating: 4.9,
    reviews: 4200,
    students: 87500,
    hours: 42,
    lessons: 152,
    price: 9500,
    oldPrice: 18000,
    bestseller: true,
    updated: "December 2025",
    monthsNew: 2,
    description:
      "Learn Python from beginner to advanced, including projects and real-world applications.",
    features: [
      "Master Python fundamentals and advanced concepts",
      "Learn data structures, algorithms, and best practices",
    ],
  },
  {
    id: "excel-from-zero-to-hero",
    title: "Excel from Zero to Hero",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80",
    instructor: "Excel Academy",
    category: "Microsoft Excel",
    level: "Beginner",
    rating: 4.7,
    reviews: 1050,
    students: 23100,
    hours: 18,
    lessons: 74,
    price: 8500,
    oldPrice: 15000,
    bestseller: false,
    hot: true,
    updated: "November 2025",
    monthsNew: 4,
    description: "Learn Excel formulas, pivot tables, charts, and advanced functions.",
    features: [
      "Master Excel formulas and functions",
      "Create professional charts and dashboards",
    ],
  },
  {
    id: "agentic-ai-workshop",
    title: "Agentic AI Workshop",
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=600&q=80",
    instructor: "AI Agents Lab",
    category: "AI Agents & Agentic AI",
    level: "Intermediate",
    rating: 4.8,
    reviews: 780,
    students: 9800,
    hours: 20,
    lessons: 68,
    price: 11500,
    oldPrice: 20000,
    bestseller: true,
    updated: "January 2026",
    monthsNew: 1,
    description: "Hands-on workshop to build agentic AI solutions.",
    features: [
      "Build autonomous AI agents from scratch",
      "Deploy AI agents in production environments",
    ],
  },
  {
    id: "digital-marketing-complete-guide",
    title: "Digital Marketing Complete Guide",
    image: "https://images.unsplash.com/photo-1557838923-2985c318be48?auto=format&fit=crop&w=600&q=80",
    instructor: "Marketing Pro",
    category: "Digital Marketing",
    level: "All Levels",
    rating: 4.8,
    reviews: 2300,
    students: 54200,
    hours: 35,
    lessons: 128,
    price: 12000,
    oldPrice: 24000,
    bestseller: true,
    hot: true,
    updated: "December 2025",
    monthsNew: 2,
    description: "Master SEO, Google Ads, Social Media, and Email Marketing.",
    features: [
      "Master SEO and content marketing strategies",
      "Run profitable Google Ads and Facebook Ads campaigns",
    ],
  },
  {
    id: "aws-certified-solutions-architect",
    title: "AWS Certified Solutions Architect",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80",
    instructor: "Cloud Academy",
    category: "Amazon AWS",
    level: "Intermediate",
    rating: 4.5,
    reviews: 1200,
    students: 31000,
    hours: 30,
    lessons: 110,
    price: 15000,
    oldPrice: 28000,
    bestseller: false,
    hot: true,
    updated: "January 2026",
    monthsNew: 1,
    description:
      "Get AWS certified and learn cloud architecture, EC2, S3, Lambda, and more.",
    features: [
      "Prepare for AWS Solutions Architect certification",
      "Design scalable and secure cloud architectures",
    ],
  },
  {
    id: "cyber-security-mastery",
    title: "Cyber Security Mastery",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=600&q=80",
    instructor: "InfoSec Academy",
    category: "Cyber Security",
    level: "Advanced",
    rating: 4.6,
    reviews: 161,
    students: 4700,
    hours: 28,
    lessons: 102,
    price: 10900,
    oldPrice: 19500,
    bestseller: true,
    updated: "December 2025",
    monthsNew: 2,
    description:
      "Master SOC automation and threat detection for real-world cyber security challenges.",
    features: [
      "Learn ethical hacking and penetration testing",
      "Master network security and threat detection",
    ],
  },
  {
    id: "web-development-bootcamp",
    title: "Web Development Bootcamp",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=600&q=80",
    instructor: "Dev School",
    category: "Web Development",
    level: "All Levels",
    rating: 4.7,
    reviews: 2150,
    students: 68900,
    hours: 52,
    lessons: 210,
    price: 9800,
    oldPrice: 19900,
    bestseller: true,
    hot: true,
    updated: "January 2026",
    monthsNew: 1,
    description: "Learn HTML, CSS, JS, React, Node.js and build real web apps.",
    features: [
      "Master HTML, CSS, JavaScript, and modern frameworks",
      "Build full-stack web applications with React and Node.js",
    ],
  },
  {
    id: "data-science-with-python-and-r",
    title: "Data Science with Python & R",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80",
    instructor: "Data Science Academy",
    category: "Data Science",
    level: "Intermediate",
    rating: 4.8,
    reviews: 3100,
    students: 72300,
    hours: 45,
    lessons: 168,
    price: 13500,
    oldPrice: 26000,
    bestseller: true,
    hot: true,
    updated: "January 2026",
    monthsNew: 1,
    description:
      "Master data analysis, visualization, and machine learning with Python and R.",
    features: [
      "Learn pandas, NumPy, and data manipulation techniques",
      "Create stunning visualizations with Matplotlib and Seaborn",
    ],
  },
  {
    id: "machine-learning-a-z",
    title: "Machine Learning A-Z",
    image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?auto=format&fit=crop&w=600&q=80",
    instructor: "ML Masters",
    category: "Machine Learning",
    level: "All Levels",
    rating: 4.9,
    reviews: 5400,
    students: 113000,
    hours: 44,
    lessons: 175,
    price: 14500,
    oldPrice: 27000,
    bestseller: true,
    updated: "January 2026",
    monthsNew: 1,
    description:
      "Learn supervised, unsupervised learning, deep learning, and AI techniques.",
    features: [
      "Master regression, classification, and clustering algorithms",
      "Deploy ML models to production environments",
    ],
  },
  {
    id: "blockchain-and-cryptocurrency",
    title: "Blockchain & Cryptocurrency",
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=600&q=80",
    instructor: "Crypto Academy",
    category: "Blockchain",
    level: "Intermediate",
    rating: 4.6,
    reviews: 1580,
    students: 28400,
    hours: 32,
    lessons: 118,
    price: 12800,
    oldPrice: 23000,
    bestseller: false,
    hot: true,
    updated: "December 2025",
    monthsNew: 2,
    description:
      "Understand blockchain technology, smart contracts, DeFi, and cryptocurrency.",
    features: [
      "Build and deploy smart contracts on Ethereum",
      "Understand blockchain architecture and consensus mechanisms",
    ],
  },
  {
    id: "graphic-design-masterclass",
    title: "Graphic Design Masterclass",
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=600&q=80",
    instructor: "Creative Studio Pro",
    category: "Graphic Design",
    level: "All Levels",
    rating: 4.8,
    reviews: 2890,
    students: 61500,
    hours: 38,
    lessons: 140,
    price: 11200,
    oldPrice: 22000,
    bestseller: true,
    updated: "January 2026",
    monthsNew: 1,
    description:
      "Master Adobe Photoshop, Illustrator, Figma and create stunning professional designs.",
    features: [
      "Design logos, branding materials, and marketing collateral",
      "Master typography, color theory, and composition",
    ],
  },
  {
    id: "cloud-computing-with-azure",
    title: "Cloud Computing with Azure",
    image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=600&q=80",
    instructor: "Cloud Experts Academy",
    category: "Cloud Computing",
    level: "Intermediate",
    rating: 4.7,
    reviews: 1920,
    students: 39200,
    hours: 36,
    lessons: 132,
    price: 14900,
    oldPrice: 25000,
    bestseller: false,
    hot: true,
    updated: "December 2025",
    monthsNew: 2,
    description:
      "Master Azure cloud services, deployment, and architecture for enterprise solutions.",
    features: [
      "Deploy and manage Azure virtual machines and storage",
      "Build serverless applications with Azure Functions",
    ],
  },
  {
    id: "project-management-pmp",
    title: "Project Management (PMP)",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=600&q=80",
    instructor: "PM Certification Academy",
    category: "Project Management",
    level: "Intermediate",
    rating: 4.7,
    reviews: 2240,
    students: 47800,
    hours: 40,
    lessons: 146,
    price: 13900,
    oldPrice: 24000,
    bestseller: true,
    updated: "January 2026",
    monthsNew: 1,
    description:
      "Complete PMP certification prep with Agile, Scrum, and project execution strategies.",
    features: [
      "Master PMI's PMBOK Guide and project frameworks",
      "Learn Agile, Scrum, and hybrid methodologies",
    ],
  },
  {
    id: "entrepreneurship-idea-to-ipo",
    title: "Entrepreneurship: Idea to IPO",
    image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=600&q=80",
    instructor: "Startup Success Academy",
    category: "Entrepreneurship",
    level: "All Levels",
    rating: 4.8,
    reviews: 3670,
    students: 83600,
    hours: 34,
    lessons: 122,
    price: 12900,
    oldPrice: 24500,
    bestseller: true,
    hot: true,
    updated: "January 2026",
    monthsNew: 1,
    description:
      "Build, launch, and scale your startup from idea validation to exit strategies.",
    features: [
      "Validate your business idea and find product-market fit",
      "Raise funding from angels, VCs, and crowdfunding",
    ],
  },
];

// ── Commercial subjects ("Explore Commercial Subjects") ─────────────────────
// `accent` drives the CSS 3D-style icon treatment.

export const COMMERCIAL_SUBJECTS = [
  { id: "accounting", name: "Accounting", icon: "Calculator", courses: 48, accent: ["#1e3a8a", "#3b82f6"], blurb: "Financial statements, ledgers and reporting.", description: "Master financial accounting, statements and reporting", questions: 720, difficulty: "Medium-Hard", topics: ["Ledgers", "Trial Balance", "Cash Flow", "Reports"] },
  { id: "financial-accounting", name: "Financial Accounting", icon: "BookOpen", courses: 36, accent: ["#0f766e", "#14b8a6"], blurb: "Double entry, trial balances and final accounts.", description: "Double-entry, trial balances and final accounts", questions: 640, difficulty: "Medium", topics: ["Double Entry", "Trial Balance", "Final Accounts", "Accruals"] },
  { id: "economics", name: "Economics", icon: "TrendingUp", courses: 52, accent: ["#b45309", "#f59e0b"], blurb: "Micro, macro and development economics.", description: "Understand micro and macroeconomics principles", questions: 750, difficulty: "Medium", topics: ["Micro", "Macro", "Trade", "Finance"] },
  { id: "commerce", name: "Commerce", icon: "Store", courses: 41, accent: ["#6d28d9", "#a78bfa"], blurb: "Trade, aids to trade and business organisation.", description: "Learn trade, aids to trade and business organisation", questions: 690, difficulty: "Medium", topics: ["Trade", "Banking", "Insurance", "Marketing"] },
  { id: "business-studies", name: "Business Studies", icon: "Briefcase", courses: 39, accent: ["#9d174d", "#ec4899"], blurb: "Business ownership, management and finance.", description: "Understand business ownership, management and finance", questions: 620, difficulty: "Medium", topics: ["Ownership", "Management", "Finance", "Marketing"] },
  { id: "marketing", name: "Marketing", icon: "Megaphone", courses: 33, accent: ["#c2410c", "#fb923c"], blurb: "Research, promotion and consumer behaviour.", description: "Learn research, promotion and consumer behaviour", questions: 580, difficulty: "Medium", topics: ["Research", "Promotion", "Branding", "Consumers"] },
  { id: "entrepreneurship", name: "Entrepreneurship", icon: "Rocket", courses: 28, accent: ["#4c1d95", "#8b5cf6"], blurb: "Idea validation, funding and growth.", description: "Explore idea validation, funding and growth", questions: 530, difficulty: "Medium-Hard", topics: ["Ideas", "Funding", "Launch", "Growth"] },
  { id: "book-keeping", name: "Book-keeping", icon: "Receipt", courses: 31, accent: ["#065f46", "#10b981"], blurb: "Source documents, day books and cash books.", description: "Study source documents, day books and cash books", questions: 600, difficulty: "Easy-Medium", topics: ["Source Docs", "Day Books", "Cash Book", "Petty Cash"] },
  { id: "banking-finance", name: "Banking & Finance", icon: "Landmark", courses: 27, accent: ["#1e40af", "#60a5fa"], blurb: "Banking operations, credit and monetary policy.", description: "Understand banking operations, credit and monetary policy", questions: 560, difficulty: "Medium-Hard", topics: ["Operations", "Credit", "Policy", "Investments"] },
  { id: "insurance", name: "Insurance", icon: "ShieldCheck", courses: 22, accent: ["#0e7490", "#22d3ee"], blurb: "Risk, policies and claims management.", description: "Learn risk, policies and claims management", questions: 480, difficulty: "Medium", topics: ["Risk", "Policies", "Claims", "Underwriting"] },
  { id: "taxation", name: "Taxation", icon: "Percent", courses: 25, accent: ["#7c2d12", "#f97316"], blurb: "Tax principles, computation and compliance.", description: "Master tax principles, computation and compliance", questions: 500, difficulty: "Hard", topics: ["Principles", "Computation", "VAT", "Compliance"] },
  { id: "business-management", name: "Business Management", icon: "Users", courses: 34, accent: ["#3730a3", "#818cf8"], blurb: "Planning, organising and leading teams.", description: "Learn planning, organising and leading teams", questions: 540, difficulty: "Medium", topics: ["Planning", "Organising", "Leading", "Control"] },
];

// ── Authenticated landing: platform hero cards ──────────────────────────────
// `icon` is a lucide-react export name resolved in the component (keeps this
// data module free of JSX/icon imports).

export const PLATFORM_CARDS = [
  {
    id: "study",
    title: "Explore Study Platform",
    description: "Prepare for WAEC, NECO and JAMB with structured lessons, notes and past questions.",
    cta: "Start studying",
    href: "https://study.filloptech.com/",
    icon: "GraduationCap",
    accent: ["#1d4ed8", "#60a5fa"],
  },
  {
    id: "cbt",
    title: "CBT Guru",
    description: "Practise realistic computer-based tests and track your readiness for exam day.",
    cta: "Take a CBT",
    href: "https://cbt.filloptech.com/",
    icon: "MonitorCheck",
    accent: ["#111827", "#374151"],
  },
  {
    id: "research",
    title: "Accelerate Your Research with Expert Resources",
    description: "Find projects, seminar topics and expert-guided research materials in one place.",
    cta: "Explore research",
    href: "https://business.filloptech.com/research",
    icon: "BookOpenCheck",
    accent: ["#7c3aed", "#c084fc"],
  },
  {
    id: "exam-pins",
    title: "Get Your Exams Pin",
    description: "Buy authentic exam pins for WAEC, NECO, NABTEB and more — instantly.",
    cta: "Buy a pin",
    href: "https://business.filloptech.com/exam-pins/purchase",
    icon: "KeyRound",
    accent: ["#ea580c", "#fbbf24"],
  },
];

// ── Books ───────────────────────────────────────────────────────────────────

export const LANDING_BOOKS = [
  { id: "book-further-maths", title: "Further Mathematics for Senior Secondary", author: "Prof. A. Adeyemi", category: "Mathematics", pages: 412, price: 6500, oldPrice: 9000, rating: 4.8, image: "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?auto=format&fit=crop&w=600&q=80" },
  { id: "book-chemistry-practical", title: "Practical Chemistry for WAEC & NECO", author: "Dr. N. Okafor", category: "Chemistry", pages: 288, price: 5200, oldPrice: 7500, rating: 4.7, image: "https://images.unsplash.com/photo-1532012197267-da84d127e765?auto=format&fit=crop&w=600&q=80" },
  { id: "book-use-of-english", title: "The Complete Use of English", author: "Mrs. C. Bello", category: "English", pages: 350, price: 4800, oldPrice: 6800, rating: 4.9, image: "https://images.unsplash.com/photo-1512820790803-83ca734da794?auto=format&fit=crop&w=600&q=80" },
  { id: "book-economics-compendium", title: "Economics Compendium for JAMB", author: "Mr. T. Nwosu", category: "Economics", pages: 396, price: 5900, oldPrice: 8200, rating: 4.6, image: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=600&q=80" },
  { id: "book-biology-made-easy", title: "Biology Made Easy", author: "Dr. F. Balogun", category: "Biology", pages: 440, price: 7000, oldPrice: 9800, rating: 4.8, image: "https://images.unsplash.com/photo-1535905557558-afc4877a26fc?auto=format&fit=crop&w=600&q=80" },
  { id: "book-government-text", title: "Government for Senior Secondary Schools", author: "Mr. I. Musa", category: "Government", pages: 372, price: 5500, oldPrice: 7600, rating: 4.5, image: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=600&q=80" },
  { id: "book-commerce-guide", title: "Commerce: A Complete Guide", author: "Mrs. G. Eze", category: "Commerce", pages: 320, price: 5100, oldPrice: 7200, rating: 4.6, image: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=600&q=80" },
  { id: "book-physics-formulas", title: "Physics Formulas & Solved Problems", author: "Engr. O. Okoli", category: "Physics", pages: 298, price: 6300, oldPrice: 8800, rating: 4.7, image: "https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?auto=format&fit=crop&w=600&q=80" },
];

// ── Project topics ──────────────────────────────────────────────────────────

export const LANDING_PROJECTS = [
  { id: "proj-1", title: "Design and Implementation of a Student Result Management System", field: "Computer Science", level: "Final Year", description: "A web-based system for computing, storing and publishing student results with role-based access.", href: "https://business.filloptech.com/research" },
  { id: "proj-2", title: "The Impact of Social Media on Academic Performance of Secondary Students", field: "Education", level: "Undergraduate", description: "A study of how social media usage correlates with study habits and outcomes.", href: "https://business.filloptech.com/research" },
  { id: "proj-3", title: "Effect of Government Expenditure on Economic Growth in Nigeria", field: "Economics", level: "Undergraduate", description: "An econometric analysis of public spending and GDP growth over a decade.", href: "https://business.filloptech.com/research" },
  { id: "proj-4", title: "Automated Teller Machine Fraud Detection Using Machine Learning", field: "Computer Science", level: "Final Year", description: "A model that flags anomalous ATM transactions in real time.", href: "https://business.filloptech.com/research" },
];

// ── Seminar topics ──────────────────────────────────────────────────────────

export const LANDING_SEMINARS = [
  { id: "sem-1", title: "Cybersecurity Awareness for Tertiary Institutions", field: "Computer Science", duration: "45 mins", presenter: "Dr. A. Okafor", description: "Practical guidance on phishing, password hygiene and data protection for students and staff.", href: "https://business.filloptech.com/research" },
  { id: "sem-2", title: "Entrepreneurship: Turning Ideas into Sustainable Ventures", field: "Business", duration: "60 mins", presenter: "Mrs. N. Bello", description: "From idea validation to funding — a roadmap for student entrepreneurs.", href: "https://business.filloptech.com/research" },
  { id: "sem-3", title: "Renewable Energy and the Future of Power in Nigeria", field: "Engineering", duration: "50 mins", presenter: "Engr. T. Musa", description: "Solar, wind and hydro opportunities for a decentralised grid.", href: "https://business.filloptech.com/research" },
  { id: "sem-4", title: "Data Science for Decision Making in Public Health", field: "Health Science", duration: "40 mins", presenter: "Dr. F. Eze", description: "How data analytics supports disease surveillance and policy.", href: "https://business.filloptech.com/research" },
];

// ── Journals ────────────────────────────────────────────────────────────────

export const LANDING_JOURNALS = [
  { id: "journal-1", title: "Journal of Educational Research & Innovation", publisher: "Fillop Academic Press", topic: "Education", year: 2026, price: 3500, oldPrice: 5000, image: "https://images.unsplash.com/photo-1589998059171-988d887df646?auto=format&fit=crop&w=600&q=80" },
  { id: "journal-2", title: "African Journal of Applied Sciences", publisher: "Fillop Academic Press", topic: "Science", year: 2026, price: 4200, oldPrice: 6000, image: "https://images.unsplash.com/photo-1532153975070-2e9ab71f1b14?auto=format&fit=crop&w=600&q=80" },
  { id: "journal-3", title: "Journal of Business & Management Studies", publisher: "Fillop Academic Press", topic: "Business", year: 2026, price: 3800, oldPrice: 5500, image: "https://images.unsplash.com/photo-1585829365295-ab7cd400c167?auto=format&fit=crop&w=600&q=80" },
  { id: "journal-4", title: "Nigerian Journal of Engineering & Technology", publisher: "Fillop Academic Press", topic: "Engineering", year: 2026, price: 4500, oldPrice: 6500, image: "https://images.unsplash.com/photo-1517976487492-5750f3195933?auto=format&fit=crop&w=600&q=80" },
  { id: "journal-5", title: "Journal of Health Sciences & Research", publisher: "Fillop Academic Press", topic: "Health Science", year: 2026, price: 4000, oldPrice: 5800, image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80" },
  { id: "journal-6", title: "Journal of Computing & Information Technology", publisher: "Fillop Academic Press", topic: "Technology", year: 2026, price: 4300, oldPrice: 6200, image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=600&q=80" },
];
